import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { animate, type PanInfo, useMotionValue } from 'motion/react'

import { useScoreStore } from '~/stores/score'

import { isCardKnown, isCardSnoozedBySwipe, isCardWithinLimit } from '../../utils/card'

import { DRAG_SCALE, EXIT_DURATION_SECONDS, KEYBOARD_MOVE_DISTANCE } from './const'
import { type UseDraggableFlipCardOptions } from './types'
import { getDragRotationDegrees, getExitTargets, updateScoreFromSwipeDelta } from './utils'

export const useDraggableFlipCard = ({
  interactive = true,
  exitDelta,
  onCardUnknown,
  onCardSnoozed,
  onFadingEnd,
  onFadingStart,
}: UseDraggableFlipCardOptions) => {
  const x = useMotionValue(exitDelta?.x ?? 0)
  const y = useMotionValue(exitDelta?.y ?? 0)
  const rotate = useMotionValue(exitDelta?.rotate ?? 0)
  const scale = useMotionValue(exitDelta?.scale ?? 1)
  const opacity = useMotionValue(1)

  const [isKeyboardDragging, setIsKeyboardDragging] = useState(false)
  const [isPointerDragging, setIsPointerDragging] = useState(false)
  const isExitingRef = useRef(!!exitDelta)
  const keyboardOffsetRef = useRef({ x: 0, y: 0 })
  // prevent click action during dragging a card
  const suppressClickAfterDragRef = useRef(false)

  const clean = useScoreStore(state => state.clean)

  const syncRotationFromOffset = (offsetX: number) => {
    rotate.set(getDragRotationDegrees(offsetX))
  }

  const snapBackToOrigin = () => {
    const snapBackTransition = { duration: 0.28, ease: 'easeOut' as const }

    void animate(x, 0, snapBackTransition)
    void animate(y, 0, snapBackTransition)
    void animate(rotate, 0, snapBackTransition)
    keyboardOffsetRef.current = { x: 0, y: 0 }
  }

  const commitSwipe = (deltaX: number, deltaY: number) => {
    const isSnoozedSwipe = isCardSnoozedBySwipe(deltaX, deltaY)
    const isHorizontalSwipe = !isSnoozedSwipe && !isCardWithinLimit(deltaX)
    if (!isHorizontalSwipe && !isSnoozedSwipe) return false

    if (isSnoozedSwipe) onCardSnoozed?.()
    if (isHorizontalSwipe && !isCardKnown(deltaX)) onCardUnknown?.()

    isExitingRef.current = true
    onFadingStart?.(deltaX, deltaY, getDragRotationDegrees(deltaX), DRAG_SCALE)

    return true
  }

  useEffect(() => {
    if (!exitDelta) return

    const exitTargets = getExitTargets(exitDelta.x, exitDelta.y)
    const transition = { duration: EXIT_DURATION_SECONDS, ease: 'easeOut' as const }

    void Promise.all([
      animate(x, exitTargets.x, transition),
      animate(y, exitTargets.y, transition),
      animate(rotate, exitTargets.rotate, transition),
      animate(opacity, 0, transition),
    ]).then(() => onFadingEnd?.(exitDelta.x, exitDelta.y))
    // Exit animation should run once when the ghost card mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once exit
  }, [])

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!interactive || isExitingRef.current) return

    if (event.key === 'Escape' && isKeyboardDragging) {
      event.preventDefault()
      setIsKeyboardDragging(false)
      snapBackToOrigin()
      clean()

      return
    }

    if (event.key === ' ') {
      event.preventDefault()

      if (!isKeyboardDragging) {
        setIsKeyboardDragging(true)
        keyboardOffsetRef.current = { x: x.get(), y: y.get() }

        return
      }

      const { x: deltaX, y: deltaY } = keyboardOffsetRef.current
      setIsKeyboardDragging(false)
      clean()

      if (!commitSwipe(deltaX, deltaY)) snapBackToOrigin()

      return
    }

    if (!isKeyboardDragging) return

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      keyboardOffsetRef.current = {
        x: keyboardOffsetRef.current.x - KEYBOARD_MOVE_DISTANCE,
        y: keyboardOffsetRef.current.y,
      }
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      keyboardOffsetRef.current = {
        x: keyboardOffsetRef.current.x + KEYBOARD_MOVE_DISTANCE,
        y: keyboardOffsetRef.current.y,
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      keyboardOffsetRef.current = {
        x: keyboardOffsetRef.current.x,
        y: keyboardOffsetRef.current.y + KEYBOARD_MOVE_DISTANCE,
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      keyboardOffsetRef.current = {
        x: keyboardOffsetRef.current.x,
        y: keyboardOffsetRef.current.y - KEYBOARD_MOVE_DISTANCE,
      }
    } else return

    const { x: deltaX, y: deltaY } = keyboardOffsetRef.current
    x.set(deltaX)
    y.set(deltaY)
    syncRotationFromOffset(deltaX)
    updateScoreFromSwipeDelta(deltaX, deltaY)
  }

  const handleDragStart = () => {
    if (isExitingRef.current) return

    suppressClickAfterDragRef.current = true
    setIsPointerDragging(true)
  }

  const handleDrag = (_: unknown, info: PanInfo) => {
    syncRotationFromOffset(info.offset.x)
    updateScoreFromSwipeDelta(info.offset.x, info.offset.y)
  }

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    setIsPointerDragging(false)
    if (isExitingRef.current) return

    const deltaX = info.offset.x
    const deltaY = info.offset.y
    clean()

    if (!commitSwipe(deltaX, deltaY)) snapBackToOrigin()
  }

  const handleClick = (clickEvent: { preventDefault: () => void }) => {
    if (!suppressClickAfterDragRef.current) return
    clickEvent.preventDefault()
    suppressClickAfterDragRef.current = false
  }

  const isDragging = isKeyboardDragging || isPointerDragging
  const isDragEnabled = interactive && !exitDelta

  return {
    handleClick,
    handleDrag,
    handleDragEnd,
    handleDragStart,
    handleKeyDown,
    isDragEnabled,
    isDragging,
    motionStyle: { x, y, rotate, scale, opacity },
  }
}
